import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
// Mixpanel (initialized later with a key you'll provide)
import mixpanel from 'mixpanel-browser';
// Stripe imports
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { fetchWithMetaAttribution } from '../../utils/metaAttribution';
import { stageTikTokCompletePayment, trackTikTokCompletePayment } from '../../utils/tiktokPixel';
import { STRIPE_PUBLISHABLE_KEY } from '../../config/publicConfig';

// DE 2.99 EURO


// Meta Pixel integration
declare global {
  interface Window {
    fbq: any;
  }
}

// Helper function to safely track Meta Pixel events
const trackMetaPixel = (eventName: string, params?: any) => {
  try {
    if (typeof window !== 'undefined' && window.fbq && window.fbq.loaded) {
      window.fbq('track', eventName, params);
      return true;
    } else if (typeof window !== 'undefined' && window.fbq) {
      // If pixel exists but not loaded yet, queue the event
      window.fbq('track', eventName, params);
      return true;
    }
  } catch (error) {
    console.error('Meta Pixel tracking error:', error);
  }
  return false;
};

// Stripe config
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const API_BASE = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? 'http://localhost:4242' : '';

// wf13 pricing configuration
const TRIAL_PRICE_ID = 'price_1T6F6bHsfUxB19MUZbKwAARK';
const REGULAR_PRICE_ID = 'price_1T6F7QHsfUxB19MU9LZQzCdw';
// Replace this placeholder with the Stripe Price ID for the €0.99 exit offer.
const EXIT_OFFER_PRICE_ID = 'price_1UGNQrHsfUxB19MU3ryoMFdF';
const TRIAL_AMOUNT_CENTS = 299;
const EXIT_OFFER_AMOUNT_CENTS = 99;

// CSS for animations
const animationStyles = `
  @keyframes wave {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(10, 19, 18, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(10, 19, 18, 0);
    }
  }
  
  .wave-animation {
    animation: wave 1.4s infinite;
  }

  @keyframes scroll-left {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-33.333%);
    }
  }

  @keyframes scroll-right {
    0% {
      transform: translateX(-33.333%);
    }
    100% {
      transform: translateX(0);
    }
  }

  @keyframes scroll-up {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-50%);
    }
  }

  .animate-scroll-left {
    animation: scroll-left 12s linear infinite;
  }

  .animate-scroll-right {
    animation: scroll-right 12s linear infinite;
  }

  .animate-scroll-up {
    animation: scroll-up 37.5s linear infinite;
  }

  @keyframes exit-offer-backdrop-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes exit-offer-panel-in {
    from {
      opacity: 0;
      transform: translateY(32px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .exit-offer-backdrop {
    animation: exit-offer-backdrop-in 180ms ease-out both;
  }

  .exit-offer-panel {
    animation: exit-offer-panel-in 320ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    .exit-offer-backdrop,
    .exit-offer-panel {
      animation: none;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = animationStyles;
  document.head.appendChild(styleSheet);
}

// Inline Payment Form Component
const InlinePaymentForm: React.FC<{
  mode: 'payment' | 'setup';
  onSuccess: () => void;
  onSetupSuccess?: (paymentMethodId: string) => void;
}> = ({ mode, onSuccess, onSetupSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    if (mode === 'payment') {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) {
        setError(confirmError.message || 'Zahlung fehlgeschlagen');
        setSubmitting(false);
        return;
      }
      onSuccess();
    } else {
      const { error: setupError, setupIntent } = await (stripe as any).confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (setupError) {
        setError(setupError.message || 'Einrichtung fehlgeschlagen');
        setSubmitting(false);
        return;
      }
      const pmId = (setupIntent as any)?.payment_method || (setupIntent as any)?.payment_method_id;
      if (pmId && onSetupSuccess) onSetupSuccess(pmId);
    }
  };

  return (
    <div className="w-full">
      <PaymentElement
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card']
        }}
      />
      {error && <div className="text-red-300 text-sm mt-3 p-3 bg-red-900 bg-opacity-30 rounded-lg">{error}</div>}
      <button
        type="button"
        onClick={handleConfirm}
        disabled={submitting || !stripe}
        className={`w-full h-14 mt-6 font-bold text-lg transition-all duration-200 ${submitting ? 'bg-[#9CAFC0] text-[#FDF9F2] cursor-not-allowed' : 'bg-[#315F86] text-[#FDF9F2] hover:bg-[#254B6A] shadow-lg'}`}
        style={{ borderRadius: '10px' }}
      >
        {submitting ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></div>
            <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1.4s' }}></div>
            <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1.4s' }}></div>
          </div>
        ) : 'Zahlung bestätigen'}
      </button>
    </div>
  );
};

interface Wf13Props { }

const Wf13: React.FC<Wf13Props> = () => {

  // State management
  const [step, setStep] = useState<number>(1); // 1-5 = quiz steps, 6 = payment, 7 = success
  const backgroundGradient = useMemo(() => (
    step === 1
      ? 'linear-gradient(180deg, rgba(243, 236, 223, 1) 0%, rgba(220, 232, 242, 0) 40%, rgba(243, 236, 223, 1) 100%)'
      : 'linear-gradient(to top, #C8D9E7, #F3ECDF)'
  ), [step]);
  const [uniqueUserId] = useState<string>(() => {
    // Generate unique browser ID
    const stored = localStorage.getItem('watchi_user_id');
    if (stored) return stored;

    const newId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('watchi_user_id', newId);
    return newId;
  });

  const [creatingSubscription, setCreatingSubscription] = useState<boolean>(false);
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [applePayReady, setApplePayReady] = useState<boolean>(false);
  const [applePayNotSupported, setApplePayNotSupported] = useState<boolean>(false);
  const [applePayFailCount, setApplePayFailCount] = useState<number>(0);
  const [applePayInitAttempts, setApplePayInitAttempts] = useState<number>(0);
  const [preInitializedPR, setPreInitializedPR] = useState<any>(null);
  const [applePayInitializing, setApplePayInitializing] = useState<boolean>(true);
  const [paymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const [showExitOffer, setShowExitOffer] = useState<boolean>(false);
  const [exitOfferPaymentRequest, setExitOfferPaymentRequest] = useState<any>(null);
  const [exitOfferApplePayReady, setExitOfferApplePayReady] = useState<boolean>(false);
  const [exitOfferApplePayInitializing, setExitOfferApplePayInitializing] = useState<boolean>(true);
  const [toast, setToast] = useState<{ message: string, type: 'error' | 'success' } | null>(null);

  const creatingSubscriptionRef = useRef(creatingSubscription);
  const requestInProgressRef = useRef(requestInProgress);

  useEffect(() => {
    if (step !== 5) return;
    trackTikTokCompletePayment({
      content_id: TRIAL_PRICE_ID,
      content_type: 'product',
      content_name: 'Watchi Premium Subscription',
      quantity: 1,
      price: TRIAL_AMOUNT_CENTS / 100,
      value: TRIAL_AMOUNT_CENTS / 100,
      currency: 'EUR',
    }, `${window.location.pathname}:${TRIAL_PRICE_ID}`);
  }, [step]);

  React.useEffect(() => {
    creatingSubscriptionRef.current = creatingSubscription;
  }, [creatingSubscription]);

  React.useEffect(() => {
    requestInProgressRef.current = requestInProgress;
  }, [requestInProgress]);

  // Card payment states
  const [showPaymentForm, setShowPaymentForm] = useState<boolean>(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);

  // Server logging function
  const trackClientEvent = useCallback(async (event: string, data: any) => {
    try {
      await fetch(`${API_BASE}/api/track-client-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event,
          data,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        })
      });
    } catch (error) {
      console.error('Failed to log to server:', error);
    }
  }, []);

  // Init Mixpanel once
  React.useEffect(() => {
    try {
      const MIXPANEL_TOKEN = (process.env.REACT_APP_MIXPANEL_TOKEN as string) || (window as any)?.MIXPANEL_TOKEN;
      if (MIXPANEL_TOKEN && (mixpanel as any)?.init) {
        (mixpanel as any).init(MIXPANEL_TOKEN, { debug: false });
      }
    } catch (error) {
      // console.error('Mixpanel init error:', error);
    }
  }, []);

  // Track step start on every step change
  React.useEffect(() => {
    try {
      if ((mixpanel as any)?.track) {
        (mixpanel as any).track(`wf13_step_${step}_start`, { step, source: 'web', quiz_number: 2 });
      }
    } catch (_) {
      // no-op
    }

    // Track Meta Pixel ViewContent event on step 1 (first step)
    if (step === 1) {
      trackMetaPixel('ViewContent', { content_name: 'Funnel Step 1' });
    }
  }, [step]);

  // Detect in-app browser
  React.useEffect(() => {
    const inAppBrowser = /FBAN|FBAV|Instagram|FB_IAB/.test(navigator.userAgent);
    // console.log('In-app browser detected:', inAppBrowser);

    try {
      const originInfo = {
        hostname: window.location.hostname,
        origin: window.location.origin,
        href: window.location.href,
        referrer: document.referrer || null,
        inAppBrowser,
      };

      trackClientEvent('apple_pay_origin_detected', originInfo).catch(() => { });
    } catch (originLogError) {
      // console.warn('Origin detection logging failed:', originLogError);
    }
  }, [trackClientEvent]);

  const showToast = useCallback((message: string, type: 'error' | 'success' = 'error') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const handleApplePayPaymentMethod = useCallback(async (
    ev: any,
    priceId: string,
    offer: 'standard' | 'exit_offer'
  ) => {
    const eventPrefix = offer === 'exit_offer' ? 'wf13_exit_offer_apple_pay' : 'wf13_apple_pay';
    try { (mixpanel as any)?.track?.(`${eventPrefix}_paymentmethod_on`, { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

    if (creatingSubscriptionRef.current || requestInProgressRef.current) {
      ev.complete('fail');
      return;
    }

    creatingSubscriptionRef.current = true;
    requestInProgressRef.current = true;
    setCreatingSubscription(true);
    setRequestInProgress(true);

    try {
      const fallbackEmail = `${uniqueUserId}@watchi.app`;
      const finalEmail = ev.payerEmail || fallbackEmail;
      setCustomerEmail(finalEmail);

      const payerName = ev.payerName || uniqueUserId;
      const shippingCountry = ev.shippingAddress?.country || ev.paymentMethod?.billing_details?.address?.country || null;

      const customerRes = await fetch(`${API_BASE}/api/stripe/create-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: finalEmail,
          userId: uniqueUserId,
          name: payerName,
          address: shippingCountry ? { country: shippingCountry } : undefined,
        }),
      });
      const customerJson = await customerRes.json();
      if (!customerRes.ok) throw new Error(customerJson?.error || 'Failed to create customer');
      const stripeCustomerId = customerJson.customerId;

      // Confirm off-session usage before creating the recurring subscription.
      try {
        const setupIntentRes = await fetch(`${API_BASE}/api/stripe/create-setup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: stripeCustomerId,
            paymentMethodId: ev.paymentMethod.id,
          }),
        });

        if (!setupIntentRes.ok) {
          const setupIntentError = await setupIntentRes.json();
          console.error(`❌ [${eventPrefix.toUpperCase()}] SetupIntent failed:`, setupIntentError);
        }
      } catch (setupIntentError) {
        console.error(`❌ [${eventPrefix.toUpperCase()}] SetupIntent error:`, setupIntentError);
      }

      const response = await fetchWithMetaAttribution(`${API_BASE}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: stripeCustomerId,
          paymentMethodId: ev.paymentMethod.id,
          priceId,
          regularPriceId: REGULAR_PRICE_ID,
          email: finalEmail,
          customerName: payerName,
          customerEmail: finalEmail,
          uniqueUserId,
          funnelId: 'wf13',
          eventSourceUrl: window.location.href,
          instantPayment: false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log(`❌ [${eventPrefix.toUpperCase()}] Subscription creation failed:`, errorData);
        try { (mixpanel as any)?.track?.(`${eventPrefix}_subscription_creation_error`, { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

        if (errorData.error && errorData.error.includes('already has an active subscription')) {
          try { (mixpanel as any)?.track?.(`${eventPrefix}_flow_existing_subscription`, { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
          ev.complete('success');
          setShowExitOffer(false);
          setPaymentProcessing(false);
          setStep(5);
          return;
        }

        throw new Error(errorData.error || 'Payment failed. Please try again.');
      }

      await response.json();
      ev.complete('success');
      try { (mixpanel as any)?.track?.(`${eventPrefix}_flow_success`, { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
      stageTikTokCompletePayment({
        content_id: priceId,
        content_type: 'product',
        content_name: offer === 'exit_offer'
          ? 'Watchi Premium Subscription Exit Offer'
          : 'Watchi Premium Subscription',
        quantity: 1,
        price: (offer === 'exit_offer' ? EXIT_OFFER_AMOUNT_CENTS : TRIAL_AMOUNT_CENTS) / 100,
        value: (offer === 'exit_offer' ? EXIT_OFFER_AMOUNT_CENTS : TRIAL_AMOUNT_CENTS) / 100,
        currency: 'EUR',
      });
      setShowExitOffer(false);
      setPaymentProcessing(false);
      setStep(5);
    } catch (error) {
      try { (mixpanel as any)?.track?.(`${eventPrefix}_paymentmethod_error`, { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
      console.error(`${eventPrefix}: Apple Pay payment error:`, error);
      ev.complete('fail');

      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'Payment failed. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      creatingSubscriptionRef.current = false;
      requestInProgressRef.current = false;
      setCreatingSubscription(false);
      setRequestInProgress(false);
      setPaymentProcessing(false);
    }
  }, [showToast, uniqueUserId]);

  // Pre-initialize Apple Pay with retry logic
  React.useEffect(() => {
    const initApplePay = (attemptNumber: number = 1) => {
      // console.log(`wf13: Starting Apple Pay initialization attempt ${attemptNumber}...`);

      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        // console.log('wf13: Not in browser environment');
        setApplePayInitializing(false);
        return;
      }

      trackClientEvent('apple_pay_init_attempt', {
        attempt: attemptNumber,
        userAgent: navigator.userAgent,
        platform: navigator.platform
      }).catch(() => { });

      stripePromise
        .then(async (stripe) => {
          if (!stripe) {
            // console.error('wf13: Stripe not loaded');
            await trackClientEvent('apple_pay_init_failed', {
              attempt: attemptNumber,
              reason: 'stripe_not_loaded',
              userAgent: navigator.userAgent,
              platform: navigator.platform
            }).catch(() => { });

            if (attemptNumber < 2) {
              // console.log('wf13: Retrying Apple Pay initialization...');
              setTimeout(() => initApplePay(attemptNumber + 1), 1000);
              return;
            }

            setApplePayNotSupported(true);
            setApplePayInitializing(false);
            return;
          }

          try {
            // console.log('wf13: Stripe loaded successfully');
            // console.log('wf13: Creating payment request with amount:', TRIAL_AMOUNT_CENTS);

            const pr = stripe.paymentRequest({
              country: 'DE',
              currency: 'eur',
              total: { label: 'Watchi Premium – Alle Zifferblätter freischalten', amount: TRIAL_AMOUNT_CENTS },
              requestPayerName: true,
              requestPayerEmail: true,
            });

            // console.log('wf13: Payment request created, registering handlers...');

            pr.on('cancel', () => {
              // console.log('wf13: Apple Pay cancelled by user');
              try { (mixpanel as any)?.track?.('wf13_apple_pay_cancel_on', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
              try { (mixpanel as any)?.track?.('wf13_exit_offer_shown', { price: 0.99, currency: 'EUR', source: 'apple_pay_cancel' }); } catch (_) { }
              setPaymentProcessing(false);
              setShowExitOffer(true);
            });

            pr.on('paymentmethod', (ev: any) => {
              handleApplePayPaymentMethod(ev, TRIAL_PRICE_ID, 'standard');
            });

            // console.log('wf13: Checking Apple Pay availability...');

            const result = await pr.canMakePayment();
            // console.log('wf13: canMakePayment result:', result);

            await trackClientEvent('apple_pay_canMakePayment_result', {
              attempt: attemptNumber,
              result: result,
              userAgent: navigator.userAgent,
              platform: navigator.platform
            }).catch(() => { });

            if (result && result.applePay) {
              // Apple Pay доступен
              // Merchant validation happens automatically via canMakePayment() and show()
              setPreInitializedPR(pr);
              setApplePayReady(true);
              setApplePayNotSupported(false);
              setApplePayInitAttempts(attemptNumber);
              setApplePayInitializing(false);

              // console.log('✅ wf13: Apple Pay initialized successfully');
              await trackClientEvent('apple_pay_init_success', {
                attempt: attemptNumber,
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                result: result,
                applePay: true
              }).catch(() => { });
            } else {
              // fallback на Card
              // console.log('❌ wf13: Apple Pay not supported - using card fallback');
              // console.log('wf13: Available payment methods:', result);

              // Check if user is in Facebook/Instagram in-app browser
              const isInAppBrowser = /FBAN|FBAV|Instagram|FB_IAB/.test(navigator.userAgent);

              await trackClientEvent('apple_pay_init_failed', {
                attempt: attemptNumber,
                reason: isInAppBrowser ? 'in_app_browser' : 'applePay_not_available',
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                result: result,
                applePay: false,
                isInAppBrowser
              }).catch(() => { });

              if (attemptNumber < 2) {
                // console.log('wf13: Retrying Apple Pay initialization...');
                setTimeout(() => initApplePay(attemptNumber + 1), 1000);
                return;
              }

              setApplePayReady(false);
              setApplePayNotSupported(true);
              setApplePayInitializing(false);
            }
          } catch (error) {
            // console.error('❌ wf13: Apple Pay pre-init error:', error);

            await trackClientEvent('apple_pay_init_error', {
              attempt: attemptNumber,
              error: error instanceof Error ? error.message : 'Unknown error',
              userAgent: navigator.userAgent,
              platform: navigator.platform
            }).catch(() => { });

            if (attemptNumber < 2) {
              // console.log('wf13: Retrying Apple Pay initialization after error...');
              setTimeout(() => initApplePay(attemptNumber + 1), 1000);
              return;
            }

            setApplePayNotSupported(true);
            setApplePayInitializing(false);
          }
        })
        .catch((error) => {
          // console.error('❌ wf13: stripePromise rejected:', error);
          setApplePayNotSupported(true);
          setApplePayInitializing(false);
        });
    };

    // Increased delay to 500ms to ensure DOM is ready
    const timer = setTimeout(() => {
      // console.log('1000 ms timeout');
      initApplePay(1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [handleApplePayPaymentMethod, trackClientEvent]);

  // Prepare a separate Apple Pay request for the €0.99 exit offer so the CTA
  // can open the native sheet directly from the user's click.
  React.useEffect(() => {
    let isActive = true;

    stripePromise
      .then(async (stripe) => {
        if (!stripe || !isActive) {
          if (isActive) setExitOfferApplePayInitializing(false);
          return;
        }

        const paymentRequest = stripe.paymentRequest({
          country: 'DE',
          currency: 'eur',
          total: {
            label: 'Watchi Premium – Alle Zifferblätter freischalten',
            amount: EXIT_OFFER_AMOUNT_CENTS,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        paymentRequest.on('cancel', () => {
          try { (mixpanel as any)?.track?.('wf13_exit_offer_apple_pay_cancel', { price: 0.99, currency: 'EUR' }); } catch (_) { }
          setPaymentProcessing(false);
        });

        paymentRequest.on('paymentmethod', (ev: any) => {
          handleApplePayPaymentMethod(ev, EXIT_OFFER_PRICE_ID, 'exit_offer');
        });

        try {
          const result = await paymentRequest.canMakePayment();
          if (!isActive) return;

          if (result?.applePay) {
            setExitOfferPaymentRequest(paymentRequest);
            setExitOfferApplePayReady(true);
          } else {
            setExitOfferApplePayReady(false);
          }
        } catch (error) {
          if (isActive) {
            setExitOfferApplePayReady(false);
            console.error('wf13_exit_offer: Apple Pay initialization error:', error);
          }
        } finally {
          if (isActive) setExitOfferApplePayInitializing(false);
        }
      })
      .catch((error) => {
        if (!isActive) return;
        setExitOfferApplePayReady(false);
        setExitOfferApplePayInitializing(false);
        console.error('wf13_exit_offer: Stripe initialization error:', error);
      });

    return () => {
      isActive = false;
    };
  }, [handleApplePayPaymentMethod]);

  React.useEffect(() => {
    if (!showExitOffer) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !creatingSubscriptionRef.current && !requestInProgressRef.current) {
        try { (mixpanel as any)?.track?.('wf13_exit_offer_dismissed', { source: 'escape' }); } catch (_) { }
        setShowExitOffer(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [showExitOffer]);

  // Card Payment handler
  const handleStartCardPayment = useCallback(async () => {
    // console.log('💳 Card payment button clicked');
    try { (mixpanel as any)?.track?.('wf13_card_click', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

    if (creatingSubscription || requestInProgress) {
      // console.log('⚠️ Already processing payment');
      return;
    }

    setCreatingSubscription(true);
    setRequestInProgress(true);
    try {
      const email = `${uniqueUserId}@watchi-temp.com`;
      setCustomerEmail(email);

      // Create customer
      const customerRes = await fetch(`${API_BASE}/api/stripe/create-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const customerJson = await customerRes.json();
      if (!customerRes.ok) throw new Error(customerJson?.error || 'Failed to create customer');
      const customerId = customerJson.customerId;
      setCustomerId(customerId);

      // Create SetupIntent for card payment
      const siRes = await fetch(`${API_BASE}/api/stripe/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      const siJson = await siRes.json();
      if (!siRes.ok) throw new Error(siJson?.error || 'Failed to create setup intent');
      setSetupClientSecret(siJson.clientSecret);
      setShowPaymentForm(true);

      // Track card payment flow - form shown
      try { (mixpanel as any)?.track?.('wf13_card_flow', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
    } catch (e) {
      // console.error('Card payment error:', e);
      showToast('Fehler beim Starten der Zahlung. Bitte versuchen Sie es erneut.');
    } finally {
      setCreatingSubscription(false);
      setRequestInProgress(false);
      setPaymentProcessing(false); // Сбрасываем флаг обработки
    }
  }, [creatingSubscription, requestInProgress, uniqueUserId, showToast]);

  // Apple Pay handler (simplified)
  const handleStartApplePay = useCallback(async () => {
    // console.log('🍎 Apple Pay button clicked');
    try { (mixpanel as any)?.track?.('wf13_apple_pay_click', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

    if (preInitializedPR && applePayReady) {
      try { (mixpanel as any)?.track?.('wf13_apple_pay_flow', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
      try {
        await preInitializedPR.show();
      } catch (showError) {
        // console.log('Apple Pay show error:', showError);
        const newFailCount = applePayFailCount + 1;
        setApplePayFailCount(newFailCount);
        const errorMessage = (showError as any)?.message || 'Apple Pay failed. Please try again.';
        try { (mixpanel as any)?.track?.(errorMessage, { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }
        showToast(errorMessage);
        setPaymentProcessing(false);
        // console.log(`Apple Pay attempt ${newFailCount} failed`);
      }
    } else {
      // console.log('Apple Pay not ready or not available');
      setPaymentProcessing(false);
    }
  }, [preInitializedPR, applePayReady, applePayFailCount, showToast]);

  const dismissExitOffer = useCallback((source: 'close_button' | 'backdrop' | 'decline') => {
    if (creatingSubscriptionRef.current || requestInProgressRef.current || paymentProcessing) return;
    try { (mixpanel as any)?.track?.('wf13_exit_offer_dismissed', { source }); } catch (_) { }
    setShowExitOffer(false);
  }, [paymentProcessing]);

  const handleStartExitOfferApplePay = useCallback(async () => {
    if (paymentProcessing || creatingSubscription || requestInProgress) return;

    if (!exitOfferPaymentRequest || !exitOfferApplePayReady) {
      showToast('Apple Pay ist gerade nicht verfügbar. Bitte versuchen Sie es erneut.');
      return;
    }

    setPaymentProcessing(true);
    try { (mixpanel as any)?.track?.('wf13_exit_offer_cta_click', { price: 0.99, currency: 'EUR', payment_method: 'apple_pay' }); } catch (_) { }
    trackMetaPixel('InitiateCheckout', {
      content_name: 'Watchi Subscription Exit Offer',
      value: EXIT_OFFER_AMOUNT_CENTS / 100,
      currency: 'EUR',
    });

    try {
      await exitOfferPaymentRequest.show();
    } catch (error) {
      setPaymentProcessing(false);
      const message = error instanceof Error ? error.message : 'Apple Pay failed. Please try again.';
      // The native sheet also emits "cancel"; cancellation is not a payment error.
      if (!/cancel|abort/i.test(message)) {
        try { (mixpanel as any)?.track?.('wf13_exit_offer_apple_pay_show_error', { message }); } catch (_) { }
        showToast(message);
      }
    }
  }, [creatingSubscription, exitOfferApplePayReady, exitOfferPaymentRequest, paymentProcessing, requestInProgress, showToast]);

  // Show card form after 2 failed Apple Pay attempts
  React.useEffect(() => {
    if (applePayFailCount >= 2 && applePayReady && !applePayNotSupported) {
      handleStartCardPayment();
    }
  }, [applePayFailCount, applePayReady, applePayNotSupported, handleStartCardPayment]);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const agreeToTerms = true;
  const [carouselInterval, setCarouselInterval] = useState<NodeJS.Timeout | null>(null);

  // App screenshots for carousel - Watch 6, 10, 9, 8, 11, 1
  const slides = React.useMemo(() => [
    {
      image: `/images/figma_wf7/Watch 8.png`
    },
    {
      image: `/images/figma_wf7/Watch 6.png`
    },
    {
      image: `/images/figma_wf7/Watch 10.png`
    },
    {
      image: `/images/figma_wf7/Watch 9.png`
    },
    {
      image: `/images/figma_wf7/Watch 1.png`
    },
    {
      image: `/images/figma_wf7/Watch 11.png`
    }
  ], []); // Empty dependency array ensures this only runs once

  // Auto-scroll carousel every 1.75 seconds (2x faster)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 1750);
    setCarouselInterval(interval);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Touch handlers for swipe
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      // Reset carousel timer when user swipes
      if (carouselInterval) {
        clearInterval(carouselInterval);
      }

      if (isLeftSwipe) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
      if (isRightSwipe) {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      }

      // Restart timer
      const newInterval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      setCarouselInterval(newInterval);
    }
  };

  // Payment handler for Get Now buttons
  const handleGetNow = useCallback(async () => {
    if (!agreeToTerms) {
      showToast('Bitte stimmen Sie den Nutzungsbedingungen und der Datenschutzrichtlinie zu');
      return;
    }

    // Защита от множественных кликов
    if (paymentProcessing || creatingSubscription || requestInProgress) {
      // console.log('⚠️ Payment already in progress, ignoring click');
      return;
    }

    // Устанавливаем флаг обработки платежа
    setPaymentProcessing(true);

    // Track Mixpanel event for step 4 CTA click
    try {
      if ((mixpanel as any)?.track) {
        (mixpanel as any).track('wf13_step_4_cta_click', { step: 4, source: 'web', quiz_number: 2 });
      }
    } catch (_) {
      // no-op
    }

    // Track Meta Pixel InitiateCheckout event
    trackMetaPixel('InitiateCheckout', {
      content_name: 'Watchi Subscription',
      value: TRIAL_AMOUNT_CENTS / 100,
      currency: 'EUR',
    });

    // Use preInitializedPR if available, otherwise fallback to card
    // No need to re-check canMakePayment - if preInitializedPR exists, it was already validated
    // If Apple Pay becomes unavailable, pr.show() will handle the error
    const applePayAvailable = applePayReady && !applePayNotSupported && !!preInitializedPR;

    const flowDecision = {
      applePayReady,
      applePayNotSupported,
      preInitializedPR: !!preInitializedPR,
      applePayInitAttempts,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
    };

    // console.log('🔍 Payment flow decision:', { ...flowDecision, applePayAvailable });

    if (applePayAvailable) {
      // console.log('✅ Choosing Apple Pay flow');
      trackClientEvent('payment_flow_decision', { ...flowDecision, applePayAvailable, chosen_flow: 'apple_pay' });
      handleStartApplePay();
    } else {
      // console.log('💳 Choosing Card flow');
      trackClientEvent('payment_flow_decision', { ...flowDecision, applePayAvailable, chosen_flow: 'card' });
      handleStartCardPayment();
    }
  }, [agreeToTerms, paymentProcessing, creatingSubscription, requestInProgress, applePayReady, applePayNotSupported, applePayInitAttempts, preInitializedPR, handleStartApplePay, handleStartCardPayment, showToast, trackClientEvent]);

  return (
    <div className="min-h-screen w-full relative">
      {/* Fixed background gradient - only for steps other than 1 */}
      {step !== 1 && (
        <div
          className="fixed inset-0 w-full h-screen z-0"
          style={{ background: backgroundGradient }}
        ></div>
      )}

      {/* Content wrapper */}
      <div className="relative z-10 max-w-md mx-auto">
        {/* Toast */}
        {toast && (
          <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-[90] px-4 py-2 rounded-lg text-[#FDF9F2] font-medium ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
            }`}>
            {toast.message}
          </div>
        )}

        {/* Step 1: Figma design replication */}
        {step === 1 && (
          <div className="h-screen w-full flex flex-col overflow-hidden" style={{ backgroundColor: '#F3ECDF' }}>
            {/* Background image back 1-min.png */}
            <div
              className="fixed inset-0 w-full h-screen"
              style={{
                backgroundImage: 'radial-gradient(circle at 50% 23%, rgba(255,255,255,0.98) 0%, rgba(220,232,242,0.96) 38%, rgba(243,236,223,0.99) 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                pointerEvents: 'none',
                zIndex: 0
              }}
            ></div>

            <div
              className="flex-1 relative overflow-hidden"
              style={{
                paddingTop: 'calc(env(safe-area-inset-top, 0px) + 50px)',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 200px)',
                zIndex: 10
              }}
            >
              {/* Top image - 10% left margin, moved up 10px */}
              <div className="w-full mb-4" style={{ marginLeft: '10%', position: 'relative', marginTop: '-10px' }}>
                <img
                  src="/images/wf7_step1_img.png"
                  alt="Premium Zifferblätter Collage"
                  className="h-auto"
                  style={{ width: 'auto', maxWidth: '90%', transform: 'scaleY(1)', transformOrigin: 'top center', objectFit: 'contain', display: 'block' }}
                />
              </div>

              <div className="w-full max-w-[360px] mx-auto flex flex-col items-center text-center relative px-2">
                {/* Dialify icon with the existing flash treatment */}
                <div className="mb-4 flex flex-col items-center justify-center relative" style={{ marginTop: '0px' }}>
                  <img
                    src="/Dialify_Icon.jpg"
                    alt="Dialify logo"
                    className="h-auto relative z-10 rounded-2xl"
                    style={{ width: 'auto', maxWidth: '69px', maxHeight: '69px' }}
                  />
                  {/* flash.png под лого с альфой 50%, уменьшен на 20% */}
                  <img
                    src="/images/figma_wf7/flash.png"
                    alt="Flash effect"
                    className="absolute"
                    style={{
                      width: 'auto',
                      maxWidth: '134px',
                      opacity: 0.5,
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 0
                    }}
                  />
                </div>

                {/* Premium style text - Apple Watch Faces Gallery first, font size reduced by 2px */}
                <h1
                  className="uppercase mb-4"
                  style={{
                    fontFamily: 'Urbanist, sans-serif',
                    fontWeight: 900,
                    fontStyle: 'italic',
                    fontSize: 'clamp(18px, 10vw, 36px)',
                    lineHeight: '1.05',
                    width: '90%',
                    maxWidth: '100%',
                    background: 'linear-gradient(138deg, rgba(49, 95, 134, 1) 6%, rgba(116, 151, 181, 1) 81%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Apple Watch ZIFFERBLÄTTER
                </h1>

                {/* Trusted by 300k users with branches - moved up 5px, branches 15% larger */}
                <div className="flex items-center justify-center gap-3 mb-4" style={{ marginTop: '-5px' }}>
                  {/* Vector 1.svg left */}
                  <img
                    src="/images/dialify/branch-left.svg"
                    alt="Left branch"
                    style={{ width: 'auto', height: '37px' }}
                  />
                  <div className="text-center">
                    <div className="text-[#1B3651] text-sm" style={{ fontFamily: 'Urbanist, sans-serif', fontWeight: 500 }}>
                      Vertraut von
                    </div>
                    <div className="text-[#1B3651] text-lg font-bold" style={{ fontFamily: 'Urbanist, sans-serif' }}>
                      300k Nutzern
                    </div>
                  </div>
                  {/* Vector 2.svg right */}
                  <img
                    src="/images/dialify/branch-right.svg"
                    alt="Right branch"
                    style={{ width: 'auto', height: '37px' }}
                  />
                </div>
              </div>
            </div>

            {/* Fixed CTA Button with Supports label above - white background with black text, no red overlay */}
            <div className="fixed bottom-0 left-0 right-0 z-50 px-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
              {/* Supports any model text - fixed above button, reduced margin */}
              <div className="px-2 py-2 inline-block rounded-full mb-1.5" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', width: '90%', maxWidth: '100%', margin: '0 auto', display: 'block' }}>
                <span
                  className="text-[#1B3651] text-center block whitespace-nowrap"
                  style={{
                    fontFamily: 'Urbanist, sans-serif',
                    fontWeight: 600,
                    fontSize: 'clamp(10px, 4.2vw, 14px)',
                    lineHeight: '1.3'
                  }}
                >
                  Unterstützt jedes Apple Watch Modell
                </span>
              </div>
              <div className="pt-4">
                <button
                  onClick={() => setStep(2)}
                  className="w-full h-16 font-semibold text-xl shadow-lg transition-all duration-200 bg-[#315F86] text-[#FDF9F2] hover:bg-[#254B6A] active:scale-[0.98]"
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Watch faces gallery with carousels */}
        {step === 2 && (
          <div className="min-h-screen w-full flex flex-col" style={{ backgroundColor: '#F3ECDF' }}>
            {/* Background image back 1-min.png */}
            <div
              className="fixed inset-0 w-full h-screen"
              style={{
                backgroundImage: 'radial-gradient(circle at 50% 23%, rgba(255,255,255,0.98) 0%, rgba(220,232,242,0.96) 38%, rgba(243,236,223,0.99) 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                pointerEvents: 'none',
                zIndex: 0
              }}
            ></div>

            <div className="flex-1 relative" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 25px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 220px)', zIndex: 10 }}>
              {/* Three rows of watch faces - full width, infinite loop */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2.1px' }}>
                {/* Row 1 - Moving left - infinite loop - 5 unique faces duplicated multiple times */}
                <div className="overflow-hidden">
                  <div className="flex gap-4 animate-scroll-left" style={{ width: '300%' }}>
                    {[
                      '/images/figma_wf7/face_17_preview.png',
                      '/images/figma_wf7/face_19_preview.png',
                      '/images/figma_wf7/face_21_preview.png',
                      '/images/figma_wf7/face_23_preview-2.png',
                      '/images/figma_wf7/face_25_preview.png',
                      '/images/figma_wf7/face_17_preview.png',
                      '/images/figma_wf7/face_19_preview.png',
                      '/images/figma_wf7/face_21_preview.png',
                      '/images/figma_wf7/face_23_preview-2.png',
                      '/images/figma_wf7/face_25_preview.png',
                      '/images/figma_wf7/face_17_preview.png',
                      '/images/figma_wf7/face_19_preview.png',
                      '/images/figma_wf7/face_21_preview.png',
                      '/images/figma_wf7/face_23_preview-2.png',
                      '/images/figma_wf7/face_25_preview.png'
                    ].map((src, i) => (
                      <div key={i} className="flex-shrink-0 rounded-xl overflow-hidden" style={{ width: '120.65px', height: '144.4px' }}>
                        <img src={src} alt="Watch face" className="w-full h-full object-contain" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 2 - Moving right - infinite loop - 5 unique faces duplicated multiple times */}
                <div className="overflow-hidden">
                  <div className="flex gap-4 animate-scroll-right" style={{ width: '300%' }}>
                    {[
                      '/images/figma_wf7/face_28_preview.png',
                      '/images/figma_wf7/face_30_preview-2.png',
                      '/images/figma_wf7/face_31_preview.png',
                      '/images/figma_wf7/face_32_preview.png',
                      '/images/figma_wf7/face_33_preview.png',
                      '/images/figma_wf7/face_28_preview.png',
                      '/images/figma_wf7/face_30_preview-2.png',
                      '/images/figma_wf7/face_31_preview.png',
                      '/images/figma_wf7/face_32_preview.png',
                      '/images/figma_wf7/face_33_preview.png',
                      '/images/figma_wf7/face_28_preview.png',
                      '/images/figma_wf7/face_30_preview-2.png',
                      '/images/figma_wf7/face_31_preview.png',
                      '/images/figma_wf7/face_32_preview.png',
                      '/images/figma_wf7/face_33_preview.png'
                    ].map((src, i) => (
                      <div key={i} className="flex-shrink-0 rounded-xl overflow-hidden" style={{ width: '120.65px', height: '144.4px' }}>
                        <img src={src} alt="Watch face" className="w-full h-full object-contain" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 3 - Moving left - infinite loop - new case faces */}
                <div className="overflow-hidden">
                  <div className="flex gap-4 animate-scroll-left" style={{ width: '300%' }}>
                    {[
                      '/images/figma_wf7/Case.png',
                      '/images/figma_wf7/Case-1 (1).png',
                      '/images/figma_wf7/Case-2 (1).png',
                      '/images/figma_wf7/Case-3 (1).png',
                      '/images/figma_wf7/Case-4 (1).png',
                      '/images/figma_wf7/Case.png',
                      '/images/figma_wf7/Case-1 (1).png',
                      '/images/figma_wf7/Case-2 (1).png',
                      '/images/figma_wf7/Case-3 (1).png',
                      '/images/figma_wf7/Case-4 (1).png',
                      '/images/figma_wf7/Case.png',
                      '/images/figma_wf7/Case-1 (1).png',
                      '/images/figma_wf7/Case-2 (1).png',
                      '/images/figma_wf7/Case-3 (1).png',
                      '/images/figma_wf7/Case-4 (1).png'
                    ].map((src, i) => (
                      <div key={i} className="flex-shrink-0 rounded-xl overflow-hidden" style={{ width: '120.65px', height: '144.4px' }}>
                        <img src={src} alt="Watch face" className="w-full h-full object-contain" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed CTA Button with labels above */}
            <div className="fixed bottom-0 left-0 right-0 z-50 px-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
              {/* Title - moved above CTA */}
              <div className="text-center mb-4 pt-8" style={{ marginTop: '15px' }}>
                <h1
                  className="uppercase text-[#1B3651]"
                  style={{
                    fontFamily: 'Urbanist, sans-serif',
                    fontWeight: 900,
                    fontStyle: 'italic',
                    fontSize: 'clamp(15px, 10vw, 33px)',
                    lineHeight: '1.05',
                    width: '95%',
                    maxWidth: '100%',
                    margin: '0 auto',
                    marginBottom: '8px'
                  }}
                >
                  Lux-Zifferblätter<br />für die Watch
                </h1>
              </div>
              <div className="pt-4">
                <button
                  onClick={() => setStep(3)}
                  className="w-full h-16 font-semibold text-xl shadow-lg transition-all duration-200 bg-[#315F86] text-[#FDF9F2] hover:bg-[#254B6A] active:scale-[0.98]"
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Reviews scrolling vertically */}
        {step === 3 && (
          <div className="min-h-screen w-full flex flex-col" style={{ backgroundColor: '#F3ECDF' }}>
            {/* Background gradient overlay - same as step 1 */}
            <div
              className="fixed inset-0 w-full h-screen z-0"
              style={{
                background: 'linear-gradient(180deg, rgba(243, 236, 223, 1) 0%, rgba(220, 232, 242, 0) 40%, rgba(243, 236, 223, 1) 100%)',
                pointerEvents: 'none'
              }}
            ></div>

            <div className="flex-1 px-6 relative z-10" style={{ paddingTop: '0px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 120px)' }}>
              {/* Scrolling reviews - visible always, infinite scroll */}
              <div className="relative overflow-hidden" style={{ height: 'calc(100vh - 210px)', minHeight: '390px' }}>
                <div className="animate-scroll-up space-y-4" style={{ willChange: 'transform', paddingBottom: '10px' }}>
                  {/* Review cards - 15 reviews (3x more) */}
                  {[
                    { username: "@sarah_m", title: "Beste Zifferblätter", text: "Tolle Zifferblätter zur Auswahl und es funktioniert nicht nur mit Apple. Sehr empfehlenswert, probiert es selbst aus", rating: 5, date: "Sep 21" },
                    { username: "@mike_johnson92", title: "Perfekte Anpassung", text: "Ich liebe, wie einfach es ist, mein Zifferblatt anzupassen. Die Hintergründe sind atemberaubend und passen perfekt zu meinem Stil", rating: 5, date: "Okt 5" },
                    { username: "@emily.chen", title: "Jeden Cent wert", text: "Die Vielfalt an Zifferblättern ist unglaublich. Ich wechsle täglich und es wird nie langweilig. Beste App für Apple Watch Anpassung", rating: 5, date: "Aug 12" },
                    { username: "@james_r", title: "Super für Training", text: "Perfekte Zifferblätter zum Verfolgen meiner Sportziele. Die Datenanzeige ist klar und die Designs sind modern", rating: 5, date: "Jul 28" },
                    { username: "@lisa_martinez", title: "Wunderschöne Designs", text: "Die Zifferblätter sind absolut wunderschön. Ich liebe es, sie zu meinen Outfits zu kombinieren. Diese App ist unverzichtbar", rating: 5, date: "Jun 15" },
                    { username: "@david_k", title: "Muss-haben App", text: "Wenn du eine Apple Watch hast, brauchst du diese App. Die Sammlung von Zifferblättern und Hintergründen ist der Wahnsinn", rating: 5, date: "Sep 8" },
                    { username: "@anna_williams", title: "Schlicht und modern", text: "Ich liebe die minimalistischen Zifferblätter. Sie sehen professionell und elegant aus. Genau das, was ich gesucht habe", rating: 5, date: "Apr 22" },
                    { username: "@ryan_07", title: "Unglaubliche Auswahl", text: "So viele Zifferblätter zur Auswahl! Ich benutze die App seit Monaten und entdecke immer noch neue Favoriten", rating: 5, date: "Sep 3" },
                    { username: "@sophia.brown", title: "Hintergründe sind toll", text: "Die Hintergründe, die mit den Zifferblättern kommen, sind atemberaubend. Sie verbessern das Gesamtbild wirklich", rating: 5, date: "Okt 18" },
                    { username: "@alex_t", title: "Einfach zu bedienen", text: "Super intuitive Benutzeroberfläche. Zifferblatt in Sekunden gewechselt. Die Qualität ist auch erstklassig", rating: 5, date: "Aug 30" },
                    { username: "@maria_garcia", title: "Täglich neue Designs", text: "Ich wechsle mein Zifferblatt jeden Tag und diese App hat endlose Möglichkeiten. Hält meine Apple Watch frisch und aufregend", rating: 5, date: "Jul 10" },
                    { username: "@chris_89", title: "Premium Qualität", text: "Die Premium-Zifferblätter sind es absolut wert. Die Liebe zum Detail ist unglaublich", rating: 5, date: "Jun 2" },
                    { username: "@jessica_l", title: "Beste Investition", text: "Beste App, die ich für meine Apple Watch heruntergeladen habe. Die Zifferblätter lassen sie sich wie ein komplett neues Gerät anfühlen", rating: 5, date: "Okt 19" },
                    { username: "@robert_smith", title: "Künstlerische Designs", text: "Die Zifferblätter sind wie Kunstwerke. Jedes einzelne ist wunderschön gestaltet und durchdacht", rating: 5, date: "Apr 5" },
                    { username: "@natalie_kim", title: "Sehr empfehlenswert", text: "Diese App hat mein Apple Watch Erlebnis verändert. Die Anpassungsmöglichkeiten sind endlos und alle Designs sind von hoher Qualität", rating: 5, date: "Sep 14" }
                  ].map((review, i) => (
                    <div key={i} className="bg-[#FBF7F0] bg-opacity-90 rounded-xl p-4 shadow-lg">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-[#1B3651] font-bold text-base">{review.title}</h3>
                        <span className="text-[#1B3651] text-sm">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-orange-500">{'★'.repeat(review.rating)}</div>
                        <span className="text-[#1B3651] text-sm">von {review.username}</span>
                      </div>
                      <p className="text-[#1B3651] text-sm leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                  {/* Duplicate for infinite scroll */}
                  {[
                    { username: "@sarah_m", title: "Beste Zifferblätter", text: "Tolle Zifferblätter zur Auswahl und es funktioniert nicht nur mit Apple. Sehr empfehlenswert, probiert es selbst aus", rating: 5, date: "Sep 21" },
                    { username: "@mike_johnson92", title: "Perfekte Anpassung", text: "Ich liebe, wie einfach es ist, mein Zifferblatt anzupassen. Die Hintergründe sind atemberaubend und passen perfekt zu meinem Stil", rating: 5, date: "Okt 5" },
                    { username: "@emily.chen", title: "Jeden Cent wert", text: "Die Vielfalt an Zifferblättern ist unglaublich. Ich wechsle täglich und es wird nie langweilig. Beste App für Apple Watch Anpassung", rating: 5, date: "Aug 12" },
                    { username: "@james_r", title: "Super für Training", text: "Perfekte Zifferblätter zum Verfolgen meiner Fitnessziele. Die Datenanzeige ist klar und die Designs sind modern", rating: 5, date: "Jul 28" },
                    { username: "@lisa_martinez", title: "Wunderschöne Designs", text: "Die Zifferblätter sind absolut wunderschön. Ich liebe es, sie zu meinen Outfits zu kombinieren. Diese App ist unverzichtbar", rating: 5, date: "Jun 15" },
                    { username: "@david_k", title: "Muss-haben App", text: "Wenn du eine Apple Watch hast, brauchst du diese App. Die Sammlung von Zifferblättern und Hintergründen ist der Wahnsinn", rating: 5, date: "Sep 8" },
                    { username: "@anna_williams", title: "Schlicht und modern", text: "Ich liebe die minimalistischen Zifferblätter. Sie sehen professionell und elegant aus. Genau das, was ich gesucht habe", rating: 5, date: "Apr 22" },
                    { username: "@ryan_07", title: "Unglaubliche Auswahl", text: "So viele Zifferblätter zur Auswahl! Ich benutze die App seit Monaten und entdecke immer noch neue Favoriten", rating: 5, date: "Sep 3" },
                    { username: "@sophia.brown", title: "Hintergründe sind toll", text: "Die Hintergründe, die mit den Zifferblättern kommen, sind atemberaubend. Sie verbessern das Gesamtbild wirklich", rating: 5, date: "Okt 18" },
                    { username: "@alex_t", title: "Einfach zu bedienen", text: "Super intuitive Benutzeroberfläche. Zifferblatt in Sekunden gewechselt. Die Qualität ist auch erstklassig", rating: 5, date: "Aug 30" },
                    { username: "@maria_garcia", title: "Täglich neue Designs", text: "Ich wechsle mein Zifferblatt jeden Tag und diese App hat endlose Möglichkeiten. Hält meine Apple Watch frisch und aufregend", rating: 5, date: "Jul 10" },
                    { username: "@chris_89", title: "Premium Qualität", text: "Die Premium-Zifferblätter sind es absolut wert. Die Liebe zum Detail ist unglaublich", rating: 5, date: "Jun 2" },
                    { username: "@jessica_l", title: "Beste Investition", text: "Beste App, die ich für meine Apple Watch heruntergeladen habe. Die Zifferblätter lassen sie sich wie ein komplett neues Gerät anfühlen", rating: 5, date: "Okt 19" },
                    { username: "@robert_smith", title: "Künstlerische Designs", text: "Die Zifferblätter sind wie Kunstwerke. Jedes einzelne ist wunderschön gestaltet und durchdacht", rating: 5, date: "Apr 5" },
                    { username: "@natalie_kim", title: "Sehr empfehlenswert", text: "Diese App hat mein Apple Watch Erlebnis verändert. Die Anpassungsmöglichkeiten sind endlos und alle Designs sind von hoher Qualität", rating: 5, date: "Sep 14" }
                  ].map((review, i) => (
                    <div key={`dup-${i}`} className="bg-[#FBF7F0] bg-opacity-90 rounded-xl p-4 shadow-lg">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-[#1B3651] font-bold text-base">{review.title}</h3>
                        <span className="text-[#1B3651] text-sm">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-orange-500">{'★'.repeat(review.rating)}</div>
                        <span className="text-[#1B3651] text-sm">von {review.username}</span>
                      </div>
                      <p className="text-[#1B3651] text-sm leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Fixed CTA Button with labels above */}
            <div className="fixed bottom-0 left-0 right-0 z-50 px-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
              {/* Title - moved above CTA, white text, slightly higher */}
              <div className="text-center mb-4" style={{ paddingTop: 'calc(24px + 0px)' }}>
                <h1
                  className="uppercase text-[#1B3651] font-bold"
                  style={{
                    fontFamily: 'Urbanist, sans-serif',
                    fontWeight: 900,
                    fontStyle: 'italic',
                    fontSize: 'clamp(15px, 10vw, 33px)',
                    lineHeight: '1.05'
                  }}
                >
                  BESTBEWERTET
                  VON NUTZERN
                </h1>
              </div>
              <div className="pt-4">
                <button
                  onClick={() => setStep(4)}
                  className="w-full h-16 font-semibold text-xl shadow-lg transition-all duration-200 bg-[#315F86] text-[#FDF9F2] hover:bg-[#254B6A] active:scale-[0.98]"
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                >
                  Weiter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Current wf13 design with white Apple Pay button */}
        {step === 4 && (
          <div className="min-h-screen w-full flex flex-col" style={{ backgroundColor: '#F3ECDF' }}>
            {/* Background image back 1-min.png */}
            <div
              className="fixed inset-0 w-full h-screen"
              style={{
                backgroundImage: 'radial-gradient(circle at 50% 23%, rgba(255,255,255,0.98) 0%, rgba(220,232,242,0.96) 38%, rgba(243,236,223,0.99) 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                pointerEvents: 'none',
                zIndex: 0
              }}
            ></div>

            {/* Background gradient overlay - same as step 1 */}
            <div
              className="fixed inset-0 w-full h-screen"
              style={{
                background: 'linear-gradient(180deg, rgba(243, 236, 223, 1) 0%, rgba(220, 232, 242, 0) 40%, rgba(220, 232, 242, 0) 100%)',
                pointerEvents: 'none',
                zIndex: 1
              }}
            ></div>

            {/* Faces.png background image with 20% opacity */}
            <div
              className="fixed inset-0 w-full h-screen"
              style={{
                backgroundImage: 'url(/images/figma_wf7/Faces.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.2,
                pointerEvents: 'none',
                zIndex: 2
              }}
            ></div>
            <div className="flex-1 relative overflow-y-auto" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 40px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)', zIndex: 10 }}>
              {/* Current carousel content - showing 3 slides at a time */}
              <div className="mb-8 relative">
                {/* Stats labels above carousel - absolutely positioned */}
                <div className="absolute top-0 left-0 right-0 flex justify-center items-center gap-8 px-6" style={{ top: '30px', zIndex: 10, flexWrap: 'nowrap' }}>
                  {/* 98% Satisfied Users */}
                  <div className="flex items-center gap-2">
                    <img
                      src="/images/dialify/branch-left.svg"
                      alt="Left branch"
                      style={{ width: 'auto', height: '42.55px' }}
                    />
                    <div className="text-center">
                      <div className="text-[#1B3651] font-bold text-2xl" style={{ fontFamily: 'Urbanist, sans-serif' }}>98%</div>
                      <div className="text-[#1B3651] text-sm" style={{ fontFamily: 'Urbanist, sans-serif' }}>Zufriedene Nutzer</div>
                    </div>
                    <img
                      src="/images/dialify/branch-right.svg"
                      alt="Right branch"
                      style={{ width: 'auto', height: '42.55px' }}
                    />
                  </div>

                  {/* 1M+ Installs Total */}
                  <div className="flex items-center gap-2">
                    <img
                      src="/images/dialify/branch-left.svg"
                      alt="Left branch"
                      style={{ width: 'auto', height: '42.55px' }}
                    />
                    <div className="text-center">
                      <div className="text-[#1B3651] font-bold text-2xl" style={{ fontFamily: 'Urbanist, sans-serif' }}>2M+</div>
                      <div className="text-[#1B3651] text-sm" style={{ fontFamily: 'Urbanist, sans-serif' }}>Installationen gesamt</div>
                    </div>
                    <img
                      src="/images/dialify/branch-right.svg"
                      alt="Right branch"
                      style={{ width: 'auto', height: '42.55px' }}
                    />
                  </div>
                </div>
                <div
                  className="relative h-[500px] overflow-hidden"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{ marginTop: '-40px' }}
                >
                  <div className="flex gap-5 transition-transform duration-1000 ease-in-out h-full"
                    style={{ transform: `translateX(calc(-30% - ${currentSlide * 40}%))` }}>
                    {/* Duplicate slides for seamless infinite loop - show 3 at a time */}
                    {[...slides, ...slides, ...slides, ...slides].map((slide, index) => {
                      const actualIndex = index % slides.length;

                      return (
                        <div
                          key={`${actualIndex}-${index}`}
                          className="flex-shrink-0 h-full"
                          style={{ width: '40%' }}
                        >
                          <div className="h-full flex items-center justify-center">
                            <img
                              src={slide.image}
                              alt={`Apple Watch ${actualIndex + 1}`}
                              className="w-full h-full object-contain"
                              style={{ maxWidth: '100%', maxHeight: '100%' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Labels under carousel */}
                <div className="text-center mt-6 px-6" style={{ marginTop: '-89px' }}>
                  <h1
                    className="text-[#1B3651] uppercase mb-2"
                    style={{
                      fontFamily: 'Urbanist, sans-serif',
                      fontWeight: 900,
                      fontStyle: 'italic',
                      fontSize: '38px',
                      lineHeight: '38px',
                      letterSpacing: '-0.04em',
                      textAlign: 'center',
                      textTransform: 'uppercase'
                    }}
                  >
                    Alle Premium Zifferblätter
                  </h1>
                  <p className="text-[#1B3651] text-lg mb-1" style={{ fontFamily: 'Urbanist, sans-serif' }}>
                    Vollzugang für nur 2,99 €
                  </p>
                  <p
                    className="text-[#1B3651]"
                    style={{
                      fontFamily: 'Urbanist, sans-serif',
                      fontWeight: 500,
                      fontStyle: 'normal',
                      fontSize: '18px',
                      lineHeight: '18px',
                      letterSpacing: '0%',
                      textAlign: 'center'
                    }}
                  >
                    Exklusive Designs 1 Woche testen
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <div className="px-6 relative" style={{ marginTop: '-5px', zIndex: 10 }}>
                <button
                  onClick={handleGetNow}
                  disabled={creatingSubscription || requestInProgress || applePayInitializing || paymentProcessing}
                  className={`w-full h-16 font-semibold text-xl shadow-lg transition-all duration-200 ${(creatingSubscription || requestInProgress)
                    ? 'bg-[#9CAFC0] text-[#FDF9F2] cursor-not-allowed'
                    : 'bg-[#315F86] text-[#FDF9F2] hover:bg-[#254B6A] active:scale-[0.98]'
                    }`}
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                >
                  {(creatingSubscription || requestInProgress) ? (
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></div>
                      <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1.4s' }}></div>
                      <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1.4s' }}></div>
                    </div>
                  ) : applePayInitializing ? (
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></div>
                      <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1.4s' }}></div>
                      <div className="w-2 h-2 bg-[#FDF9F2] rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1.4s' }}></div>
                    </div>
                  ) : (
                    'Weiter'
                  )}
                </button>
              </div>

              {/* Terms and policy text below CTA */}
              <div className="px-6 pt-8 pb-8 relative" style={{ marginTop: '0px', zIndex: 10 }}>
                <p className="text-[#5C7083] text-xs leading-relaxed" style={{ fontFamily: 'Urbanist, sans-serif' }}>
                  Mit dem Fortfahren akzeptieren Sie unsere{' '}
                  <a href="https://watchi.app/terms" target="_blank" rel="noopener noreferrer" className="text-[#5C7083] underline">Nutzungsbedingungen</a>
                  {' '}und{' '}
                  <a href="https://watchi.app/privacy" target="_blank" rel="noopener noreferrer" className="text-[#5C7083] underline">Datenschutzrichtlinie</a>
                  <br /><br />
                  Ihr Abo ist sofort nach der Zahlung verfügbar.
                  <br /><br />
                  Sie können Ihr Abo jederzeit kündigen und werden nicht erneut belastet. Keine versteckten Zahlungen. Watchi Watch Faces belastet Ihre Karte automatisch in jedem Abrechnungszeitraum, damit Sie den Zugang zu Ihrem Konto nicht verlieren. Keine Rückerstattungen oder Gutschriften, es sei denn, Sie erhalten keine Inhalte und befolgen unsere{' '}
                  <a href="https://watchi.app/cancellation" target="_blank" rel="noopener noreferrer" className="text-[#5C7083] underline">Geld-zurück-Richtlinie</a>
                  . Zum Kündigen kontaktieren Sie uns einfach: support@watchi.app oder verwalten Sie Ihr Abo in den Kontoeinstellungen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Original step 2 (success) */}
        {step === 5 && (
          <div className="min-h-screen w-full flex flex-col relative" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
            {/* Background image back 1-min.png */}
            <div
              className="fixed inset-0 w-full h-screen"
              style={{
                backgroundImage: 'radial-gradient(circle at 50% 23%, rgba(255,255,255,0.98) 0%, rgba(220,232,242,0.96) 38%, rgba(243,236,223,0.99) 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                pointerEvents: 'none',
                zIndex: 0
              }}
            ></div>

            {/* Success Image - centered on screen, full width, not cropped */}
            <div className="w-full flex items-center justify-center relative z-10" style={{ minHeight: '40vh', maxHeight: '50vh', marginTop: '70px' }}>
              <img
                src="/images/figma_wf7/step5_img.png"
                alt="Success"
                className="w-full h-full object-contain"
                style={{ maxHeight: '100%' }}
              />
            </div>

            {/* Success Text and Button - below image */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
              <div className="text-center max-w-[400px] w-full">
                {/* Success Text */}
                <h1 className="text-2xl font-bold text-[#1B3651] mb-4">
                  Zahlung erfolgreich!
                </h1>
                <p className="text-[#4F6478] text-lg mb-8">
                  Ihr Watchi Premium Abo ist jetzt aktiv. Laden Sie die App herunter, um loszulegen.
                </p>

                {/* CTA Button */}
                <button
                  onClick={() => {
                    try { (mixpanel as any)?.track?.('wf13_download_click', { source: 'web', quiz_number: 2 }); } catch (_) { }
                    const emailParam = customerEmail ? `&id=${encodeURIComponent(customerEmail)}` : '';
                    window.open(`https://watchi.onelink.me/W8qe?af_xp=custom&pid=watchi_deepLink&is_retargeting=true&af_reengagement_window=30d&deep_link_value=status&status=true${emailParam}`, '_blank');
                  }}
                  className="w-full bg-[#315F86] hover:bg-[#254B6A] text-[#FDF9F2] font-semibold py-4 px-6 rounded-lg text-lg transition-colors duration-200"
                >
                  Watchi App herunterladen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Apple Pay cancellation exit offer */}
        {showExitOffer && (
          <div
            className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wf13-exit-offer-title"
          >
            <div
              className="exit-offer-backdrop absolute inset-0 bg-[#1B3651] bg-opacity-60 backdrop-blur-md"
              onClick={() => dismissExitOffer('backdrop')}
              aria-hidden="true"
            ></div>

            <div
              className="exit-offer-panel relative w-full max-w-md overflow-y-auto rounded-t-[32px] border border-[#C6B19E] border-opacity-10 shadow-2xl sm:rounded-[32px]"
              style={{
                maxHeight: 'calc(100dvh - 12px)',
                background: 'linear-gradient(165deg, #D7E4EE 0%, #E8E1D6 52%, #1B3651 100%)',
                fontFamily: 'Urbanist, sans-serif',
              }}
            >
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden="true">
                <div
                  className="absolute -right-20 -top-24 h-64 w-64 rounded-full"
                  style={{ background: 'rgba(138, 103, 77, 0.15)', filter: 'blur(42px)' }}
                ></div>
                <div
                  className="absolute -left-24 top-44 h-52 w-52 rounded-full"
                  style={{ background: 'rgba(190, 157, 112, 0.09)', filter: 'blur(48px)' }}
                ></div>
              </div>

              <div className="relative px-6 pb-6 pt-3 sm:px-8 sm:pb-8">
                <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-[#D6E3ED] bg-opacity-90 sm:hidden"></div>

                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => dismissExitOffer('close_button')}
                    disabled={paymentProcessing || creatingSubscription || requestInProgress}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C6B19E] border-opacity-10 bg-[#FBF7F0] bg-opacity-90 text-[#1B3651] transition-colors hover:bg-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Angebot schließen"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="-mt-3 text-center">
                  <div
                    className="mx-auto mb-4 flex h-[76px] w-[76px] items-center justify-center rounded-[24px] border border-[#C6B19E] border-opacity-10 shadow-xl"
                    style={{ background: 'linear-gradient(145deg, rgba(138, 103, 77, 0.26), rgba(190, 157, 112, 0.08))' }}
                  >
                    <img
                      src="/Dialify_Icon.jpg"
                      alt="Dialify logo"
                      className="h-[58px] w-[58px] rounded-[16px] object-cover"
                    />
                  </div>

                  <span
                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.13em]"
                    style={{ color: '#315F86', backgroundColor: 'rgba(138, 103, 77, 0.11)' }}
                  >
                    Exklusives Angebot
                  </span>

                  <h2
                    id="wf13-exit-offer-title"
                    className="mx-auto mt-3 max-w-[340px] text-[30px] font-black leading-[1.02] tracking-[-0.035em] text-[#1B3651]"
                  >
                    Bleib noch kurz – spare 67 %
                  </h2>
                  <p className="mx-auto mt-3 max-w-[330px] text-[15px] leading-5 text-[#1B3651] text-opacity-70">
                    Sichere dir eine Woche Premium-Zugang zum einmaligen Sonderpreis.
                  </p>
                </div>

                <div className="mt-5 flex items-end justify-center gap-3" aria-label="Sonderpreis 0,99 Euro statt 2,99 Euro">
                  <span className="pb-1 text-lg font-semibold text-[#1B3651] text-opacity-40 line-through">2,99 €</span>
                  <span
                    className="text-[52px] font-black leading-none tracking-[-0.055em]"
                    style={{ color: '#315F86' }}
                  >
                    0,99 €
                  </span>
                </div>

                <div className="mt-5 rounded-2xl border border-[#C6B19E] border-opacity-10 bg-white bg-opacity-[0.06] px-4 py-3.5">
                  {[
                    'Alle Premium-Zifferblätter freischalten',
                    'Sofortiger Zugang nach der Zahlung',
                    'Jederzeit einfach kündbar',
                  ].map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3 py-1.5">
                      <span
                        className="flex h-5 w-5 flex-none items-center justify-center rounded-full"
                        style={{ backgroundColor: '#315F86', color: '#1B3651' }}
                        aria-hidden="true"
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-sm font-semibold text-[#1B3651] text-opacity-90">{benefit}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleStartExitOfferApplePay}
                  disabled={exitOfferApplePayInitializing || !exitOfferApplePayReady || paymentProcessing || creatingSubscription || requestInProgress}
                  className="mt-5 flex h-16 w-full items-center justify-center rounded-xl bg-white px-4 text-lg font-bold text-black shadow-xl transition-all hover:bg-gray-100 active:scale-[0.985] disabled:cursor-not-allowed disabled:active:scale-100"
                  aria-describedby="wf13-exit-offer-terms"
                >
                  {paymentProcessing || creatingSubscription || requestInProgress ? (
                    <span className="flex items-center justify-center gap-1.5" aria-label="Zahlung wird verarbeitet">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-black" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></span>
                      <span className="h-2 w-2 animate-bounce rounded-full bg-black" style={{ animationDelay: '200ms', animationDuration: '1.4s' }}></span>
                      <span className="h-2 w-2 animate-bounce rounded-full bg-black" style={{ animationDelay: '400ms', animationDuration: '1.4s' }}></span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="text-[27px] leading-none" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }} aria-hidden="true"></span>
                      <span>Mit Apple Pay für 0,99 €</span>
                    </span>
                  )}
                </button>

                {exitOfferApplePayInitializing && (
                  <p className="mt-2 text-center text-xs text-[#1B3651] text-opacity-50">Apple Pay wird vorbereitet …</p>
                )}
                {!exitOfferApplePayInitializing && !exitOfferApplePayReady && (
                  <p className="mt-2 text-center text-xs text-amber-200 text-opacity-80">
                    Apple Pay ist auf diesem Gerät nicht verfügbar.
                  </p>
                )}

                <p id="wf13-exit-offer-terms" className="mx-auto mt-3 max-w-[340px] text-center text-[11px] leading-4 text-[#1B3651] text-opacity-45">
                  Angebotspreis für die erste Woche. Danach gilt der reguläre Abopreis. Jederzeit kündbar.
                </p>

                <button
                  type="button"
                  onClick={() => dismissExitOffer('decline')}
                  disabled={paymentProcessing || creatingSubscription || requestInProgress}
                  className="mx-auto mt-3 block px-4 py-2 text-sm font-semibold text-[#1B3651] text-opacity-50 underline decoration-white decoration-opacity-20 underline-offset-4 transition-colors hover:text-opacity-80 disabled:cursor-not-allowed"
                >
                  Nein danke, Angebot ablehnen
                </button>
              </div>
            </div>
          </div>
        )}



        {/* Full Screen Payment Form Popup */}
        {showPaymentForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-[#1B3651] bg-opacity-60 backdrop-blur-sm"
              onClick={() => setShowPaymentForm(false)}
            ></div>

            {/* Popup Content */}
            <div className="relative w-full h-full max-w-md mx-auto flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 bg-gradient-to-b from-[#F3ECDF] to-transparent">
                <h2 className="text-[#1B3651] text-xl font-bold">Zahlung abschließen</h2>
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D6E3ED] bg-opacity-90 hover:bg-opacity-30 transition-all duration-200"
                >
                  <svg className="w-6 h-6 text-[#1B3651]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Payment Form Content */}
              <div className="flex-1 px-6 pb-6 overflow-y-auto">
                <div className="bg-[#FBF7F0] bg-opacity-90 backdrop-blur-sm rounded-2xl p-6">
                  {setupClientSecret && customerId && (
                    <Elements stripe={stripePromise} options={{ clientSecret: setupClientSecret, appearance: { theme: 'stripe' }, locale: 'de' }}>
                      <InlinePaymentForm
                        mode="setup"
                        onSuccess={() => { }}
                        onSetupSuccess={async (pmId) => {
                          try {
                            const email = customerEmail || `${uniqueUserId}@watchi-temp.com`;
                            const res = await fetchWithMetaAttribution(`${API_BASE}/api/stripe/create-subscription`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                customerId: customerId,
                                paymentMethodId: pmId,
                                priceId: TRIAL_PRICE_ID,
                                regularPriceId: REGULAR_PRICE_ID,
                                email: email,
                                customerName: uniqueUserId,
                                customerEmail: email,
                                uniqueUserId: uniqueUserId,
                                funnelId: 'wf13',
                                eventSourceUrl: window.location.href,
                                instantPayment: false, // wf13: instant payment - invoice paid immediately
                              }),
                            });

                            if (!res.ok) {
                              const errorData = await res.json();
                              console.log('❌ [WF13] Subscription creation failed:', errorData);
                              try { (mixpanel as any)?.track?.('wf13_card_flow_error_subscription', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

                              if (errorData.error && errorData.error.includes('already has an active subscription')) {
                                // Customer already has subscription - treat as success
                                console.log('ℹ️ [WF13] Customer already has subscription, proceeding...');

                                // Track card flow - existing subscription
                                try { (mixpanel as any)?.track?.('wf13_card_flow_existing_subscription', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

                                setPaymentProcessing(false);
                                setShowPaymentForm(false);
                                setStep(5);
                                return;
                              }

                              // Use the user-friendly error message from backend
                              const errorMessage = errorData.error || 'Subscription creation failed. Please try again.';
                              throw new Error(errorMessage);
                            }

                            await res.json();
                            // console.log('Subscription created:', result);

                            // Track card flow success
                            try { (mixpanel as any)?.track?.('wf13_card_flow_success', { plan: 'premium', source: 'web', quiz_number: 2 }); } catch (_) { }

                            // Success - go to step 5
                            setPaymentProcessing(false);
                            setShowPaymentForm(false);
                            setStep(5);
                          } catch (error) {
                            console.error('Subscription error:', error);

                            // Extract error message from response
                            let errorMessage = 'Payment failed. Please try again.';
                            if (error instanceof Error) {
                              errorMessage = error.message;
                            } else if (typeof error === 'string') {
                              errorMessage = error;
                            }

                            showToast(errorMessage, 'error');
                            setPaymentProcessing(false);
                          }
                        }}
                      />
                    </Elements>
                  )}
                </div>

                {/* Security Info */}
                <div className="mt-6 text-center">
                  <p className="text-[#1B3651] text-opacity-75 text-sm">
                    🔒 Ihre Zahlungsinformationen sind sicher und verschlüsselt
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wf13;
